import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Modal as BsModal } from 'react-bootstrap';
import { Table, Tag, Spin, Descriptions, Modal as AntModal } from 'antd';
import { ToastContainer, toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaBed,
  FaBookmark,
  FaSignInAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUnlink,
  FaSync,
  FaLayerGroup,
  FaFileExcel,
  FaUpload,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import consultaCamasDisponiblesApi from '../../../services/api/consultaCamasDisponiblesApi';
import { alojamientoProveedorApi, TIPO_OPERACION } from '../../../services/api/alojamientoProveedorApi';
import 'react-toastify/dist/ReactToastify.css';

const SYNC_ITEMS = [
  { key: 'sincronizado', label: 'Sincronizado', icon: FaCheckCircle, color: '#059669', bg: '#ecfdf5' },
  { key: 'pendiente', label: 'Pendiente envío', icon: FaClock, color: '#d97706', bg: '#fffbeb' },
  { key: 'en_cola', label: 'En cola', icon: FaSync, color: '#2563eb', bg: '#eff6ff' },
  { key: 'error', label: 'Con error', icon: FaExclamationTriangle, color: '#dc2626', bg: '#fef2f2' },
  { key: 'sin_homologar', label: 'Sin homologar', icon: FaUnlink, color: '#7c3aed', bg: '#f5f3ff' },
];

const TIPO_KEY_TO_OPERACION = {
  reservas: TIPO_OPERACION.RESERVA,
  check_in: TIPO_OPERACION.CHECKIN,
};

const truncar = (texto, max = 120) => {
  if (!texto) return '';
  const s = String(texto);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const extraerMensajeError = (error, fallback) => {
  const msg = error.response?.data?.detail || error.response?.data?.message || fallback;
  return typeof msg === 'string' ? msg : JSON.stringify(msg);
};

const KpiCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="kpi-card" style={{ '--kpi-color': color }}>
    <div className="kpi-icon-wrap">
      <Icon size={28} />
    </div>
    <div className="kpi-value">{value}</div>
    <div className="kpi-label">{label}</div>
    {sub && <div className="kpi-sub">{sub}</div>}
  </div>
);

const SyncPanel = ({ titulo, data, accent, tipoKey, onStatClick }) => {
  const total = Number(data?.total || 0);
  const handleClick = (key, label, val) => {
    if (Number(val) <= 0 || !onStatClick) return;
    onStatClick(tipoKey, key, label, titulo);
  };

  return (
    <Card className="dh-card sync-panel h-100">
      <Card.Header
        className="dh-card-header"
        style={{ background: accent, color: 'white', border: 'none' }}
      >
        <FaSync className="me-2" />
        Sync Logiflex — {titulo}
        <span className="sync-total-badge">{total} asignaciones</span>
      </Card.Header>
      <Card.Body className="p-3">
        <p className="small text-muted mb-2 sync-panel-hint">
          Clic en un número para ver detalle, exportar Excel y sincronizar
        </p>
        <div className="sync-grid">
          {SYNC_ITEMS.map(({ key, label, icon: Icon, color, bg }) => {
            const val = Number(data?.[key] || 0);
            const pct = total > 0 ? Math.round((val / total) * 100) : 0;
            const clickable = val > 0 && Boolean(onStatClick);
            return (
              <button
                key={key}
                type="button"
                className={`sync-stat sync-stat-btn ${clickable ? 'sync-stat-clickable' : 'sync-stat-disabled'}`}
                style={{ background: bg, borderColor: color }}
                disabled={!clickable}
                onClick={() => handleClick(key, label, val)}
                title={clickable ? `Ver detalle: ${label}` : 'Sin registros'}
              >
                <div className="sync-stat-head" style={{ color }}>
                  <Icon size={16} />
                  <span>{label}</span>
                </div>
                <div className="sync-stat-value sync-stat-link" style={{ color }}>
                  {val}
                </div>
                <div className="sync-stat-bar">
                  <div className="sync-stat-bar-fill" style={{ width: `${pct}%`, background: color }} />
                </div>
                <div className="sync-stat-pct">
                  {pct}%
                  {clickable && <span className="sync-hint"> · Ver detalle</span>}
                </div>
              </button>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

const ZonaCard = ({ zona, onStatClick }) => {
  const total = zona.total_camas_activas || 0;
  const disp = zona.camas_disponibles || 0;
  const pctDisp = total > 0 ? Math.round((disp / total) * 100) : 0;

  const miniStat = (tipoKey, titulo, categoria, label, count) => {
    const n = Number(count || 0);
    if (n <= 0 || !onStatClick) {
      return <span className="sync-mini-muted">{n} {label}</span>;
    }
    return (
      <button
        type="button"
        className="sync-mini-btn"
        onClick={() => onStatClick(tipoKey, categoria, label, titulo, zona.zona_id, zona.zona_nombre)}
        title={`Ver detalle ${label}`}
      >
        {n} {label}
      </button>
    );
  };

  return (
    <Card className="dh-card zona-card h-100">
      <Card.Body>
        <div className="zona-card-title">
          <FaLayerGroup className="me-2 text-primary" />
          {zona.zona_nombre}
        </div>
        <div className="zona-formula">
          <span className="zf-num">{total}</span>
          <span className="zf-op">−</span>
          <span className="zf-num zf-reserva">{zona.reservas || 0}</span>
          <span className="zf-op">−</span>
          <span className="zf-num zf-checkin">{zona.check_in || 0}</span>
          <span className="zf-op">=</span>
          <span className="zf-num zf-disp">{disp}</span>
        </div>
        <div className="zona-legend small text-muted mb-2">
          Total − Reservas − Check-in = Disponibles
        </div>
        <div className="zona-bar-wrap">
          <div className="zona-bar-labels">
            <span>Ocupación {zona.porcentaje_ocupacion || 0}%</span>
            <span>Libres {pctDisp}%</span>
          </div>
          <div className="zona-bar">
            <div
              className="zona-bar-reserva"
              style={{ width: total ? `${((zona.reservas || 0) / total) * 100}%` : '0%' }}
            />
            <div
              className="zona-bar-checkin"
              style={{ width: total ? `${((zona.check_in || 0) / total) * 100}%` : '0%' }}
            />
            <div
              className="zona-bar-libre"
              style={{ width: total ? `${(disp / total) * 100}%` : '0%' }}
            />
          </div>
        </div>
        <div className="zona-sync-mini mt-3">
          <div className="zona-sync-row">
            <span className="small fw-semibold">Reservas sync</span>
            {miniStat('reservas', 'Reservas', 'sincronizado', 'OK', zona.sync_reservas?.sincronizado)}
            {miniStat('reservas', 'Reservas', 'pendiente', 'pend.', zona.sync_reservas?.pendiente)}
            {miniStat('reservas', 'Reservas', 'error', 'err.', zona.sync_reservas?.error)}
          </div>
          <div className="zona-sync-row">
            <span className="small fw-semibold">Check-in sync</span>
            {miniStat('check_in', 'Check-in', 'sincronizado', 'OK', zona.sync_check_in?.sincronizado)}
            {miniStat('check_in', 'Check-in', 'pendiente', 'pend.', zona.sync_check_in?.pendiente)}
            {miniStat('check_in', 'Check-in', 'error', 'err.', zona.sync_check_in?.error)}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const ConsultaCamasDisponibles = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [modalSync, setModalSync] = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);
  const [enviandoId, setEnviandoId] = useState(null);
  const [sincronizandoLote, setSincronizandoLote] = useState(false);

  const [modalErrorOpen, setModalErrorOpen] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [loadingErrorDetalle, setLoadingErrorDetalle] = useState(false);

  const showToast = useCallback((type, message) => {
    const opts = { position: 'bottom-right', autoClose: 3500 };
    if (type === 'success') toast.success(message, opts);
    else if (type === 'error') toast.error(message, opts);
    else toast.warning(message, opts);
  }, []);

  const cargar = useCallback(async () => {
    if (!selectedProject?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await consultaCamasDisponiblesApi.consultar(selectedProject.id);
      if (res?.success && res?.data) {
        setData(res.data);
      } else {
        setError(res?.message || 'Respuesta inesperada del servidor.');
      }
    } catch (err) {
      const d = err.response?.data?.detail ?? err.response?.data?.message ?? err.message;
      setError(typeof d === 'string' ? d : 'No se pudo cargar la consulta.');
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filasModal = useMemo(() => {
    if (!modalSync) return [];
    const detalle = data?.detalle_sync;
    if (!detalle) return [];
    let rows = detalle[modalSync.tipoKey]?.[modalSync.categoria] || [];
    if (modalSync.zonaId) {
      rows = rows.filter((r) => Number(r.zona_id) === Number(modalSync.zonaId));
    }
    return rows;
  }, [modalSync, data]);

  const tipoOperacionModal = modalSync ? TIPO_KEY_TO_OPERACION[modalSync.tipoKey] : TIPO_OPERACION.RESERVA;
  const puedeSincronizarEnModal = ['pendiente', 'error', 'en_cola'].includes(modalSync?.categoria);
  const faltaDetalleBackend = Boolean(modalSync && data && !data.detalle_sync);

  const abrirModalSync = useCallback((tipoKey, categoria, label, tituloTipo, zonaId = null, zonaNombre = null) => {
    setSeleccionados([]);
    setModalSync({ tipoKey, categoria, label, tituloTipo, zonaId, zonaNombre });
  }, []);

  const cerrarModalSync = () => {
    setModalSync(null);
    setSeleccionados([]);
  };

  const exportarModalExcel = () => {
    if (!filasModal.length) {
      showToast('warning', 'No hay filas para exportar');
      return;
    }
    const rows = filasModal.map((r) => ({
      'ID asignación': r.asignacion_id,
      DNI: r.dni,
      Trabajador: r.trabajador_nombre,
      Estado: r.estado_asignacion,
      Zona: r.zona_nombre,
      Ubicación: r.ubicacion,
      'Cama homologada': r.cama_homologada ? 'Sí' : 'No',
      'ID Logiflex': r.cama_id_proveedor || '',
      'Estado sync': r.estado_sync || '',
      Intentos: r.intentos_sync ?? 0,
      'Respuesta sync': r.respuesta_sync || '',
      'F. reserva': r.fecha_reserva || '',
      'Check-in': r.fecha_check_in || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Detalle');
    const slug = (modalSync?.categoria || 'sync').slice(0, 20);
    XLSX.writeFile(wb, `CamasDisp_Sync_${slug}_${Date.now()}.xlsx`);
    showToast('success', `${filasModal.length} fila(s) exportada(s)`);
  };

  const sincronizarFila = async (row, forzarReenvio = false) => {
    if (!row.puede_sincronizar && !forzarReenvio) {
      showToast('warning', 'Esta asignación no puede sincronizarse');
      return;
    }
    setEnviandoId(row.asignacion_id);
    try {
      const { data: res } = await alojamientoProveedorApi.encolarReserva(row.asignacion_id, {
        forzarReenvio: forzarReenvio || row.categoria_sync === 'error' || row.categoria_sync === 'en_cola',
        origen: 'consulta_camas_disponibles',
        tipoOperacion: tipoOperacionModal,
      });
      if (res.data?.omitido) {
        showToast('warning', res.data?.motivo || res.message || 'No se encoló');
      } else {
        showToast('success', res.message || 'Encolado para sincronización');
      }
      await cargar();
    } catch (err) {
      showToast('error', extraerMensajeError(err, 'Error al sincronizar'));
    } finally {
      setEnviandoId(null);
    }
  };

  const sincronizarSeleccion = async () => {
    const ids = seleccionados.length
      ? seleccionados
      : filasModal.filter((f) => f.puede_sincronizar).map((f) => f.asignacion_id);
    if (!ids.length) {
      showToast('warning', 'No hay asignaciones sincronizables');
      return;
    }
    setSincronizandoLote(true);
    try {
      const { data: res } = await alojamientoProveedorApi.encolarBatch(ids, {
        forzarReenvio: modalSync?.categoria === 'error',
        origen: 'consulta_camas_disponibles',
        tipoOperacion: tipoOperacionModal,
      });
      showToast('success', res.message || `${res.data?.encolados || ids.length} encolado(s)`);
      await cargar();
    } catch (err) {
      showToast('error', extraerMensajeError(err, 'Error al sincronizar lote'));
    } finally {
      setSincronizandoLote(false);
    }
  };

  const abrirDetalleError = async (row) => {
    setModalErrorOpen(true);
    setErrorDetalle({ record: row, sync: null, logs: [] });
    setLoadingErrorDetalle(true);
    try {
      const { data: res } = await alojamientoProveedorApi.getLogsEnvio(
        row.asignacion_id,
        tipoOperacionModal
      );
      setErrorDetalle({
        record: row,
        sync: res.data?.sync || null,
        logs: res.data?.logs || [],
      });
    } catch (err) {
      showToast('error', extraerMensajeError(err, 'Error al cargar detalle'));
      setErrorDetalle({
        record: row,
        sync: row.respuesta_sync
          ? { respuesta_proveedor: row.respuesta_sync, intentos: row.intentos_sync }
          : null,
        logs: [],
      });
    } finally {
      setLoadingErrorDetalle(false);
    }
  };

  const columnasDetalle = useMemo(() => {
    const cols = [
      { title: 'ID', dataIndex: 'asignacion_id', width: 70 },
      { title: 'DNI', dataIndex: 'dni', width: 95 },
      { title: 'Trabajador', dataIndex: 'trabajador_nombre', ellipsis: true },
      {
        title: 'Ubicación',
        dataIndex: 'ubicacion',
        ellipsis: true,
        width: 150,
      },
      {
        title: 'Homologación',
        key: 'homolog',
        width: 100,
        render: (_, r) =>
          r.cama_homologada ? (
            <Tag color="success">OK</Tag>
          ) : (
            <Tag color="warning">Sin id</Tag>
          ),
      },
      {
        title: 'Estado sync',
        dataIndex: 'estado_sync',
        width: 95,
        render: (v) => <Tag>{v || 'pendiente'}</Tag>,
      },
    ];

    if (modalSync?.categoria === 'error' || modalSync?.categoria === 'en_cola') {
      cols.push({
        title: 'Respuesta',
        dataIndex: 'respuesta_sync',
        ellipsis: true,
        render: (v) => truncar(v, 80) || '—',
      });
    }

    cols.push({
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      render: (_, row) => (
        <div className="d-flex flex-column gap-1">
          {(modalSync?.categoria === 'error' || row.respuesta_sync) && (
            <Button
              size="sm"
              className="btn-dh-secondary py-0 px-2"
              style={{ fontSize: '0.72rem' }}
              onClick={() => abrirDetalleError(row)}
            >
              <FaExclamationTriangle size={12} className="me-1" />
              Ver error
            </Button>
          )}
          {puedeSincronizarEnModal && row.puede_sincronizar && (
            <Button
              size="sm"
              className="btn-dh-primary py-0 px-2"
              style={{ fontSize: '0.72rem' }}
              disabled={enviandoId === row.asignacion_id || sincronizandoLote}
              onClick={() => sincronizarFila(row)}
            >
              {enviandoId === row.asignacion_id ? (
                <Spin size="small" />
              ) : (
                <>
                  <FaUpload size={12} className="me-1" />
                  Sync
                </>
              )}
            </Button>
          )}
        </div>
      ),
    });

    return cols;
  }, [modalSync, puedeSincronizarEnModal, enviandoId, sincronizandoLote, tipoOperacionModal]);

  const g = data?.resumen_global || {};
  const sync = data?.sync_global || {};
  const zonas = data?.zonas || [];

  if (!selectedProject?.id) {
    return (
      <div className="dh-reporte-bg min-vh-100 d-flex align-items-center justify-content-center">
        <div className="dh-alert dh-alert-info mx-3">
          Seleccione un proyecto de campamento para continuar
        </div>
      </div>
    );
  }

  return (
    <div className="dh-reporte-bg consulta-camas-disp">
      <style>{`
        .consulta-camas-disp { min-height: 100vh; padding-bottom: 2.5rem; }
        .dh-reporte-bg { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); }
        .dh-main-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; padding: 2rem 0; margin-bottom: 2rem;
          border-radius: 0 0 30px 30px;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.25);
        }
        .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
        .dh-main-subtitle { opacity: 0.92; margin: 0.5rem 0 0; }
        .dh-card { border: none; border-radius: 20px; box-shadow: 0 12px 32px rgba(0,0,0,0.08); }
        .btn-dh-primary {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          border: none; color: white; border-radius: 12px; font-weight: 600;
        }
        .btn-dh-primary:hover:not(:disabled) { color: white; opacity: 0.95; }
        .btn-dh-primary:disabled { opacity: 0.65; }
        .btn-dh-secondary {
          background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
          border: none; color: white; border-radius: 12px; font-weight: 600;
        }
        .btn-dh-secondary:hover:not(:disabled) { color: white; opacity: 0.95; }
        .dh-alert-info {
          background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
          border: 1px solid #93c5fd; border-radius: 12px; padding: 1rem 1.25rem; color: #1e40af;
        }
        .formula-hero {
          background: white; border-radius: 24px; padding: 2rem;
          box-shadow: 0 20px 50px rgba(102, 126, 234, 0.12);
          border: 1px solid rgba(102, 126, 234, 0.15);
        }
        .formula-row {
          display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 1rem;
        }
        .formula-box {
          text-align: center; min-width: 120px; padding: 1.25rem 1.5rem; border-radius: 16px;
          font-weight: 700;
        }
        .formula-box.total { background: linear-gradient(135deg, #667eea22, #764ba222); color: #4338ca; }
        .formula-box.reserva { background: #fff7ed; color: #c2410c; }
        .formula-box.checkin { background: #ecfdf5; color: #047857; }
        .formula-box.disp {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          color: white; transform: scale(1.05);
          box-shadow: 0 12px 28px rgba(17, 153, 142, 0.35);
        }
        .formula-box .fb-val { font-size: 2.5rem; line-height: 1.1; }
        .formula-box .fb-lbl { font-size: 0.85rem; opacity: 0.9; margin-top: 0.25rem; }
        .formula-op { font-size: 2rem; font-weight: 300; color: #94a3b8; }
        .kpi-card {
          background: white; border-radius: 16px; padding: 1.25rem; text-align: center;
          border: 2px solid #e2e8f0;
        }
        .kpi-icon-wrap { color: var(--kpi-color); margin-bottom: 0.5rem; }
        .kpi-value { font-size: 1.75rem; font-weight: 800; color: var(--kpi-color); }
        .kpi-label { font-size: 0.8rem; color: #64748b; font-weight: 600; }
        .kpi-sub { font-size: 0.72rem; color: #94a3b8; margin-top: 0.2rem; }
        .ocupacion-bar { height: 14px; border-radius: 999px; background: #e2e8f0; overflow: hidden; display: flex; }
        .ocupacion-bar-reserva { background: #fb923c; }
        .ocupacion-bar-checkin { background: #34d399; }
        .sync-panel .sync-total-badge {
          float: right; font-size: 0.75rem; background: rgba(255,255,255,0.2);
          padding: 0.2rem 0.6rem; border-radius: 999px;
        }
        .sync-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; }
        .sync-panel-hint { font-size: 0.78rem; }
        .sync-stat-btn {
          width: 100%; border: 1px solid; border-radius: 12px; padding: 0.85rem;
          text-align: center; transition: transform 0.15s, box-shadow 0.15s;
          font: inherit; color: inherit;
        }
        .sync-stat-btn.sync-stat-clickable { cursor: pointer; }
        .sync-stat-btn.sync-stat-clickable:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.2);
        }
        .sync-stat-btn.sync-stat-disabled {
          cursor: default; opacity: 0.55;
        }
        .sync-stat-link { text-decoration: underline; text-underline-offset: 3px; }
        .sync-stat-head { display: flex; align-items: center; justify-content: center; gap: 0.35rem;
          font-size: 0.72rem; font-weight: 700; margin-bottom: 0.35rem; }
        .sync-stat-value { font-size: 1.5rem; font-weight: 800; }
        .sync-stat-bar { height: 4px; background: rgba(0,0,0,0.08); border-radius: 4px; margin: 0.4rem 0 0.2rem; overflow: hidden; }
        .sync-stat-bar-fill { height: 100%; border-radius: 4px; }
        .sync-stat-pct { font-size: 0.65rem; color: #64748b; }
        .sync-hint { color: #667eea; font-weight: 600; font-size: 0.68rem; }
        .sync-mini-btn {
          border: none; background: transparent; padding: 0 0.25rem;
          font-size: 0.75rem; font-weight: 600; text-decoration: underline;
          cursor: pointer; color: #2563eb;
        }
        .sync-mini-btn:hover { color: #1d4ed8; }
        .sync-mini-muted { font-size: 0.75rem; color: #94a3b8; }
        .zona-card { border-left: 4px solid #667eea; }
        .zona-card-title { font-weight: 700; font-size: 1rem; margin-bottom: 0.75rem; color: #1e293b; }
        .zona-formula { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; margin-bottom: 0.35rem; }
        .zf-num { font-weight: 800; font-size: 1.1rem; padding: 0.15rem 0.5rem; border-radius: 8px; }
        .zf-reserva { background: #fff7ed; color: #c2410c; }
        .zf-checkin { background: #ecfdf5; color: #047857; }
        .zf-disp { background: #d1fae5; color: #065f46; }
        .zf-op { color: #94a3b8; font-weight: 300; }
        .zona-bar { height: 10px; border-radius: 999px; background: #e2e8f0; display: flex; overflow: hidden; }
        .zona-bar-reserva { background: #fb923c; }
        .zona-bar-checkin { background: #34d399; }
        .zona-bar-libre { background: #a5b4fc; }
        .zona-bar-labels { display: flex; justify-content: space-between; font-size: 0.72rem; color: #64748b; margin-bottom: 0.25rem; }
        .zona-sync-row { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-bottom: 0.25rem; font-size: 0.75rem; }
        .sync-mini-ok { color: #059669; font-weight: 600; }
        .sync-mini-warn { color: #d97706; font-weight: 600; }
        .sync-mini-err { color: #dc2626; font-weight: 600; }
        .dh-modal .modal-content { border: none; border-radius: 20px; overflow: hidden; }
        .dh-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; border: none;
        }
        .dh-modal .btn-close { filter: invert(1); }
        .log-response-box {
          background: #fff5f5; border: 1px solid #fecaca; border-radius: 8px;
          padding: 10px 12px; font-size: 0.8rem; white-space: pre-wrap;
          word-break: break-word; max-height: 200px; overflow-y: auto;
        }
      `}</style>

      <ToastContainer />

      <div className="dh-main-header">
        <Container fluid="lg">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h1 className="dh-main-title d-flex align-items-center gap-2">
                <FaBed size={32} />
                Camas disponibles
              </h1>
              <p className="dh-main-subtitle">
                Proyecto: {selectedProject.nombre} — Clic en los números de sync para ver detalle
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button className="btn-dh-secondary" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-1" /> Volver
              </Button>
              <Button className="btn-dh-primary" onClick={cargar} disabled={loading}>
                <FaSync className="me-1" /> {loading ? 'Actualizando…' : 'Actualizar'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container fluid="lg">
        {error && (
          <div className="dh-alert dh-alert-info mb-4" style={{ borderColor: '#fca5a5', background: '#fef2f2', color: '#991b1b' }}>
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Cargando disponibilidad de camas…</p>
          </div>
        ) : data ? (
          <>
            <div className="formula-hero mb-4">
              <div className="formula-row">
                <div className="formula-box total">
                  <div className="fb-val">{g.total_camas_activas ?? 0}</div>
                  <div className="fb-lbl">Camas activas</div>
                </div>
                <span className="formula-op">−</span>
                <div className="formula-box reserva">
                  <div className="fb-val">{g.reservas ?? 0}</div>
                  <div className="fb-lbl">Reservas</div>
                </div>
                <span className="formula-op">−</span>
                <div className="formula-box checkin">
                  <div className="fb-val">{g.check_in ?? 0}</div>
                  <div className="fb-lbl">Check-in</div>
                </div>
                <span className="formula-op">=</span>
                <div className="formula-box disp">
                  <div className="fb-val">{g.camas_disponibles ?? 0}</div>
                  <div className="fb-lbl">Disponibles</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="ocupacion-bar">
                  <div
                    className="ocupacion-bar-reserva"
                    style={{
                      width: g.total_camas_activas
                        ? `${((g.reservas || 0) / g.total_camas_activas) * 100}%`
                        : '0%',
                    }}
                  />
                  <div
                    className="ocupacion-bar-checkin"
                    style={{
                      width: g.total_camas_activas
                        ? `${((g.check_in || 0) / g.total_camas_activas) * 100}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
            </div>

            <Row className="mb-4 g-3">
              <Col lg={6}>
                <SyncPanel
                  titulo="Reservas"
                  data={sync.reservas}
                  tipoKey="reservas"
                  accent="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                  onStatClick={abrirModalSync}
                />
              </Col>
              <Col lg={6}>
                <SyncPanel
                  titulo="Check-in"
                  data={sync.check_in}
                  tipoKey="check_in"
                  accent="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                  onStatClick={abrirModalSync}
                />
              </Col>
            </Row>

            <h5 className="fw-bold mb-3 text-secondary d-flex align-items-center gap-2">
              <FaLayerGroup /> Desglose por zona
            </h5>
            <Row className="g-3">
              {zonas.map((z) => (
                <Col key={z.zona_id} md={6} xl={4}>
                  <ZonaCard zona={z} onStatClick={abrirModalSync} />
                </Col>
              ))}
            </Row>
          </>
        ) : null}
      </Container>

      <AntModal
        open={Boolean(modalSync)}
        onCancel={cerrarModalSync}
        width={1100}
        destroyOnClose
        zIndex={1060}
        title={
          <span>
            {modalSync?.tituloTipo} — {modalSync?.label}
            {modalSync?.zonaNombre ? ` (${modalSync.zonaNombre})` : ''}
            <Tag className="ms-2">{filasModal.length}</Tag>
          </span>
        }
        footer={[
          <Button key="cerrar" className="btn-dh-secondary" onClick={cerrarModalSync}>
            Cerrar
          </Button>,
        ]}
      >
        {faltaDetalleBackend && (
          <div className="dh-alert dh-alert-info mb-3">
            El backend no devolvió <code>detalle_sync</code>. Actualice el API en staging y pulse
            Actualizar.
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button size="sm" className="btn-dh-secondary" onClick={exportarModalExcel} disabled={!filasModal.length}>
            <FaFileExcel className="me-1" /> Excel
          </Button>
          {puedeSincronizarEnModal && (
            <Button
              size="sm"
              className="btn-dh-primary"
              onClick={sincronizarSeleccion}
              disabled={sincronizandoLote || !filasModal.some((f) => f.puede_sincronizar)}
            >
              <FaUpload className="me-1" />
              {sincronizandoLote ? 'Sincronizando…' : 'Sync selección'}
            </Button>
          )}
        </div>
        <Table
          rowKey="asignacion_id"
          size="small"
          columns={columnasDetalle}
          dataSource={filasModal}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: faltaDetalleBackend ? 'Sin detalle del servidor' : 'Sin registros en esta categoría' }}
          rowSelection={
            puedeSincronizarEnModal
              ? {
                  selectedRowKeys: seleccionados,
                  onChange: (keys) => setSeleccionados(keys),
                  getCheckboxProps: (record) => ({
                    disabled: !record.puede_sincronizar,
                  }),
                }
              : undefined
          }
        />
      </AntModal>

      <BsModal show={modalErrorOpen} onHide={() => setModalErrorOpen(false)} size="lg" centered className="dh-modal" style={{ zIndex: 1070 }}>
        <BsModal.Header closeButton>
          <BsModal.Title>
            <FaExclamationTriangle className="me-2 text-danger" />
            Error sync — {errorDetalle?.record?.trabajador_nombre || 'Asignación'}
          </BsModal.Title>
        </BsModal.Header>
        <BsModal.Body>
          {loadingErrorDetalle ? (
            <div className="text-center py-4">
              <Spin tip="Cargando historial…" />
            </div>
          ) : (
            <>
              <Descriptions bordered size="small" column={2} className="mb-3">
                <Descriptions.Item label="ID">{errorDetalle?.record?.asignacion_id}</Descriptions.Item>
                <Descriptions.Item label="DNI">{errorDetalle?.record?.dni}</Descriptions.Item>
                <Descriptions.Item label="Intentos">
                  {errorDetalle?.sync?.intentos ?? errorDetalle?.record?.intentos_sync ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="Estado">
                  <Tag color="error">{errorDetalle?.sync?.estado_envio || errorDetalle?.record?.estado_sync}</Tag>
                </Descriptions.Item>
              </Descriptions>
              {(errorDetalle?.sync?.respuesta_proveedor || errorDetalle?.record?.respuesta_sync) && (
                <div className="log-response-box mb-3">
                  {errorDetalle?.sync?.respuesta_proveedor || errorDetalle?.record?.respuesta_sync}
                </div>
              )}
              {errorDetalle?.logs?.length > 0 && (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={errorDetalle.logs}
                  columns={[
                    { title: 'Fecha', dataIndex: 'created_at', width: 150 },
                    {
                      title: 'Estado',
                      dataIndex: 'estado',
                      width: 90,
                      render: (e) => <Tag color={e === 'exitoso' ? 'success' : 'error'}>{e}</Tag>,
                    },
                    { title: 'HTTP', dataIndex: 'http_status', width: 70 },
                    {
                      title: 'Respuesta',
                      dataIndex: 'response_body',
                      ellipsis: true,
                      render: (v) => truncar(v, 200),
                    },
                  ]}
                />
              )}
            </>
          )}
        </BsModal.Body>
        <BsModal.Footer>
          <Button className="btn-dh-secondary" onClick={() => setModalErrorOpen(false)}>
            Cerrar
          </Button>
        </BsModal.Footer>
      </BsModal>
    </div>
  );
};

export default ConsultaCamasDisponibles;
